import { faCheckCircle, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext } from 'react';

import PageHeader from '@/components/PageHeader';
import { Color } from '@/constants/color';
import { REQUIREMENT_FAILED, REQUIREMENT_MET } from '@/constants/treatability';
import useTreatabilityInsight from '@/hooks/useTreatabilityInsight';
import PersonContext from '@/pages/Person/PersonContext';
import Alert from '@/ui/components/Alert';
import Card from '@/ui/components/Card';
import ColorCircle from '@/ui/components/ColorCircle';
import Container from '@/ui/components/Container';
import Heading from '@/ui/components/Heading/Heading';
import IconItem from '@/ui/components/IconItem';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';

import AvailableServices from './AvailableServices';

const Treatability = () => {
  const { person } = useContext(PersonContext);

  const { isPending: isLoadingItem, data: response } = useTreatabilityInsight(person.id);

  if (isLoadingItem) {
    return <LoadingSkeleton />;
  }

  if (!response?.data) {
    return null;
  }

  const { data } = response;
  const hasOtherServicesHint =
    data.determination.result !== 'treatable' &&
    data.determination.result !== 'waitlisted' &&
    data.other_services.length > 0;

  return (
    <Container type="page">
      <div className="flex items-center">
        <div className="mb-2">
          <IconItem checked={data.determination.result === 'treatable'} />
        </div>

        <div className="capitalize">
          <PageHeader headingText={data.determination.result} />
        </div>
      </div>

      <p className="mb-2 italic well">
        {data.determination.description}
        {hasOtherServicesHint &&
          ' However, other programs may be available for this member — see Available Services below.'}
      </p>
      <hr className="mb-2" />

      <AvailableServices
        services={data.other_services}
        isInsurance={data.payment_type === 'insurance'}
      />

      <section className="mb-4">
        <div className="flex items-center mb-2">
          <FontAwesomeIcon icon={faClipboardCheck} className="mr-1 text-gray-500" />
          <Heading value={3} text="Prerequisites" />
        </div>
        {data.prerequisites.map((prerequisite) => {
          return (
            <Card key={prerequisite.label}>
              <div className="flex">
                <IconItem checked={prerequisite.result} />
                <Heading value={4} text={prerequisite.label} />
              </div>

              {prerequisite.value && (
                <>
                  {prerequisite.label === 'Birthday' ? (
                    <p className="m-1">
                      {new Date(prerequisite.value).toLocaleString([], { dateStyle: 'medium' })}
                    </p>
                  ) : (
                    <p className="m-1">{prerequisite.value}</p>
                  )}
                </>
              )}

              {prerequisite.help_text && <Alert level="warn" message={prerequisite.help_text} />}
            </Card>
          );
        })}
      </section>

      <hr className="mb-2" />

      <section className="mb-4">
        <div className="flex items-center mb-2">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-1 text-gray-500" />
          <Heading value={3} text="Requirements" />
        </div>

        {data.requirements.map((requirement) => {
          const requirementMet = requirement.result == REQUIREMENT_MET;
          const requirementFailed = requirement.result == REQUIREMENT_FAILED;
          const requirementColor = requirement.result == 'not_applicable' ? Color.BLUE : Color.GRAY;

          return (
            <Card key={requirement.label}>
              <div className="flex items-center">
                {(requirementMet || requirementFailed) && <IconItem checked={requirementMet} />}
                {!(requirementMet || requirementFailed) && <ColorCircle color={requirementColor} />}
                <Heading value={4} text={requirement.label} />
              </div>
              <p className="m-1">{requirement.description}</p>

              {requirement.help_text && <Alert level="warn" message={requirement.help_text} />}
            </Card>
          );
        })}

        <legend>
          <div className="flex items-center">
            <ColorCircle color={Color.GRAY} /> Unknown
          </div>
          <div className="flex items-center">
            <ColorCircle color={Color.RED} /> Failed
          </div>
          <div className="flex items-center">
            <ColorCircle color={Color.GREEN} /> Met
          </div>
          <div className="flex items-center">
            <ColorCircle color={Color.BLUE} /> Not Applicable
          </div>
        </legend>
      </section>
    </Container>
  );
};

export default Treatability;
